import { Lock } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { DotPill } from "@/components/ui/dot-pill"
import { UserAvatarGroup } from "@/components/ui/user-avatar-group"
import {
  deadlineCountdown,
  withDeadlineTime,
  type DeadlineFields,
} from "@/lib/bid-deadline"
import { priorityConfig } from "@/lib/status-config"
import type { Bid, BidStatus } from "@/lib/schemas"

// Format value for display (e.g., 450000 -> "$450,000")
function formatValue(value: number | undefined): string {
  if (value === undefined || value === null) return "TBD"
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value)
}

// Format deadline for display
function formatDeadline(deadline: string | undefined): string {
  if (!deadline) return "No deadline"
  try {
    const date = new Date(deadline)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  } catch {
    return "Invalid date"
  }
}

// Countdown badge. Only shown for active (pre-submission) bids — once a bid is
// submitted/won/lost/no_go the deadline is historical, so no countdown/overdue.
const COUNTDOWN_VARIANT = {
  overdue: "destructive",
  soon: "warning",
  normal: "secondary",
} as const

function getDaysUntilBadge(fields: DeadlineFields, status: BidStatus) {
  const countdown = deadlineCountdown(fields, status)
  if (!countdown) return null
  return <Badge variant={COUNTDOWN_VARIANT[countdown.tone]} className="text-xs">{countdown.short}</Badge>
}

interface BidMetadataMember {
  id: string
  userId: string
  fullName: string | null
  avatarUrl: string | null
}

interface BidMetadataStripProps {
  bid: Bid
  members?: BidMetadataMember[]
}

export function BidMetadataStrip({ bid, members }: BidMetadataStripProps) {
  const daysUntilBadge = getDaysUntilBadge(bid, bid.status)

  return (
    <div className="flex items-center gap-2 text-sm flex-wrap">
      <span className="font-medium text-foreground">{bid.client}</span>
      {bid.visibility === "restricted" && (
        <Badge variant="secondary" className="gap-1 rounded-full text-xs" dot={false}>
          <Lock className="h-3 w-3" />
          Restricted
        </Badge>
      )}
      <span className="text-muted-foreground">·</span>
      {/* Where the bid is shows in the header's stage control (R4). */}
      <DotPill
        label={priorityConfig[bid.priority]?.label ?? bid.priority}
        dotVar={priorityConfig[bid.priority]?.dotVar ?? "--muted-foreground"}
      />
      <span className="text-muted-foreground">·</span>
      <span>{formatValue(bid.value)}</span>
      <span className="text-muted-foreground">·</span>
      <span className="text-muted-foreground">
        {bid.deadline ? `Due ${withDeadlineTime(formatDeadline(bid.deadline), bid)}` : "No deadline"}
      </span>
      {daysUntilBadge}
      {members && members.length > 0 && (
        <>
          <span className="text-muted-foreground">·</span>
          <UserAvatarGroup
            userIds={members.map((m) => m.userId)}
            users={members.map((m) => ({ id: m.userId, fullName: m.fullName, avatarUrl: m.avatarUrl }))}
            maxDisplay={4}
          />
        </>
      )}
    </div>
  )
}
